/**
 * RBAC permission utilities — unnati-compatible numeric permission IDs.
 */

export type PermissionId = number

export type ActionPermissionKey =
  | 'view'
  | 'add'
  | 'update'
  | 'delete'
  | 'export'
  | 'special'

export type ActionPermissions = Partial<Record<ActionPermissionKey, PermissionId[]>>

export const EMPTY_ACTION_PERMISSIONS: ActionPermissions = {
  view: [],
  add: [],
  update: [],
  delete: [],
  export: [],
  special: [],
}

/** Extract action_permissions from various API response shapes. */
export function extractActionPermissions(raw: unknown): ActionPermissions {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_ACTION_PERMISSIONS }
  const obj = raw as Record<string, unknown>
  const nested = obj.data as Record<string, unknown> | undefined
  const perms = (obj.action_permissions ?? nested?.action_permissions) as
    | ActionPermissions
    | undefined
  if (!perms || typeof perms !== 'object') return { ...EMPTY_ACTION_PERMISSIONS }

  const toIds = (value: unknown): PermissionId[] =>
    Array.isArray(value)
      ? value.map((v) => Number(v)).filter((v) => Number.isFinite(v))
      : []

  return {
    view: toIds(perms.view),
    add: toIds(perms.add),
    update: toIds(perms.update),
    delete: toIds(perms.delete),
    export: toIds(perms.export),
    special: toIds(perms.special),
  }
}

export function hasPermissionId(
  userPermissions: PermissionId[] | undefined,
  permissionId: PermissionId,
  isAdmin = false,
): boolean {
  // Company admins have full access — they bypass per-permission checks.
  if (isAdmin) return true
  if (!userPermissions?.length) return false
  return userPermissions.includes(permissionId)
}

export function hasAnyPermissionId(
  userPermissions: PermissionId[] | undefined,
  permissionIds: PermissionId[],
  isAdmin = false,
): boolean {
  if (isAdmin) return true
  if (!permissionIds.length || !userPermissions?.length) return false
  const owned = new Set(userPermissions)
  return permissionIds.some((id) => owned.has(id))
}

/** Resolve codenames to numeric IDs using the catalog map from login. */
export function resolveCodenamesToIds(
  codenames: string[],
  codenameToId: Record<string, PermissionId> | null | undefined,
): PermissionId[] {
  if (!codenameToId || !codenames.length) return []
  return codenames
    .map((name) => codenameToId[name])
    .filter((id): id is PermissionId => typeof id === 'number')
}

export function hasCodenamePermission(
  codenames: string | string[],
  userPermissions: PermissionId[] | undefined,
  codenameToId: Record<string, PermissionId> | null | undefined,
  isAdmin = false,
  requireAll = false,
): boolean {
  if (isAdmin) return true
  const list = Array.isArray(codenames) ? codenames : [codenames]
  if (!list.length || list.every((c) => !c)) return true
  const ids = resolveCodenamesToIds(list, codenameToId)
  if (!ids.length) return false
  if (requireAll) {
    return ids.every((id) => userPermissions?.includes(id))
  }
  return hasAnyPermissionId(userPermissions, ids)
}

export function hasActionPermission(
  actionPermissions: ActionPermissions | undefined,
  userPermissions: PermissionId[] | undefined,
  action: ActionPermissionKey,
  isAdmin = false,
): boolean {
  // Company admins can perform every row action regardless of the
  // action_permissions map or which codenames they hold.
  if (isAdmin) return true
  if (!actionPermissions || !userPermissions?.length) return false
  const required = actionPermissions[action]
  if (!required?.length) return false
  const owned = new Set(userPermissions)
  return required.some((id) => owned.has(id))
}

export function hasAnyRowAction(
  actionPermissions: ActionPermissions | undefined,
  userPermissions: PermissionId[] | undefined,
  isAdmin = false,
): boolean {
  if (isAdmin) return true
  return (
    hasActionPermission(actionPermissions, userPermissions, 'update', isAdmin)
    || hasActionPermission(actionPermissions, userPermissions, 'delete', isAdmin)
    || hasActionPermission(actionPermissions, userPermissions, 'special', isAdmin)
  )
}

export interface RolePermissionAction {
  name: string
  display_name: string
  perms: PermissionId[]
  active: boolean
}

export interface RolePermissionSubmodule {
  title: string
  key: string
  sub_menu: string[]
  perms: RolePermissionAction[]
}

export interface RolePermissionModule {
  title: string
  key: string
  icon: string
  perm: RolePermissionSubmodule[]
}

export function toggleRoleMatrixAction(
  modules: RolePermissionModule[],
  submoduleKey: string,
  actionName: string,
): RolePermissionModule[] {
  return modules.map((module) => ({
    ...module,
    perm: module.perm.map((sub) => {
      if (sub.key !== submoduleKey) return sub
      return {
        ...sub,
        perms: sub.perms.map((action) => {
          if (action.name !== actionName) return action
          return { ...action, active: !action.active }
        }),
      }
    }),
  }))
}

export function collectActiveMatrixPermissions(modules: RolePermissionModule[]): PermissionId[] {
  const active = new Set<PermissionId>()
  for (const module of modules) {
    for (const sub of module.perm) {
      for (const action of sub.perms) {
        if (action.active) {
          action.perms.forEach((id) => active.add(id))
        }
      }
    }
  }
  return Array.from(active).sort((a, b) => a - b)
}
